import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, ShieldCheck, Database, Bell, Mail, Newspaper, CreditCard,
  FileText, Image as ImageIcon, Activity, LogOut, Upload, Trash2, Plus, Send, Loader2, Save, ExternalLink, Menu, X
} from "lucide-react";
import { Logo } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import api, { apiErr, fileUrl } from "@/lib/api";

const SECTIONS = [
  ["overview", "Vue générale", LayoutDashboard],
  ["users", "Utilisateurs", Users],
  ["admins", "Administrateurs", ShieldCheck],
  ["knowledge", "Base de connaissances", Database],
  ["messages", "Messages & Notifications", Bell],
  ["newsletters", "Newsletters", Newspaper],
  ["emails", "E-mails", Mail],
  ["billing", "Abonnements & Crédits", CreditCard],
  ["content", "Contenu page d'accueil", FileText],
  ["media", "Médiathèque", ImageIcon],
  ["activity", "Journal d'activité", Activity],
];

const CATEGORIES = ["Méthodologie de recherche", "Sciences de l'Information", "Sciences de la Communication",
  "Théories de la communication", "Méthodes", "Techniques de recherche", "Normes académiques",
  "Rédaction scientifique", "Bibliographie", "Études de cas", "Publications du laboratoire", "Guides institutionnels", "Autres"];

const STATUS_COLOR = { "Disponible": "bg-green-50 text-green-700", "Traitement": "bg-blue-50 text-blue-700",
  "En attente": "bg-gray-100 text-gray-600", "Erreur": "bg-red-50 text-red-700", "Archivé": "bg-gray-100 text-gray-500" };

function Stat({ label, value, sub }) {
  return <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5"><p className="text-sm text-muted2">{label}</p><p className="font-serif text-3xl font-bold text-navy mt-1">{value}</p>{sub && <p className="text-xs text-muted2">{sub}</p>}</div>;
}

export default function AdminApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  const selectSection = (key) => { setSection(key); setMobileOpen(false); };
  const currentLabel = (SECTIONS.find(([k]) => k === section) || [null, "Administration"])[1];

  return (
    <div className="h-screen flex bg-lisungi-bg overflow-hidden">
      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} data-testid="admin-backdrop" />}
      <aside className={`w-64 bg-navy flex flex-col shrink-0 transition-transform duration-300
        fixed inset-y-0 left-0 z-50 lg:static lg:z-auto lg:translate-x-0
        ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-4 flex items-start justify-between">
          <div><Logo variant="light" /><p className="text-[10px] font-mono text-lisungi-yellow mt-2 uppercase tracking-wider">Administration LARSICOM</p></div>
          <button onClick={() => setMobileOpen(false)} className="lg:hidden text-white/60 hover:text-white p-1" data-testid="admin-sidebar-close"><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
          {SECTIONS.map(([key, label, Icon]) => (
            <button key={key} onClick={() => selectSection(key)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-left transition-all ${section === key ? "bg-lisungi-yellow/15 text-lisungi-yellow font-semibold" : "text-white/70 hover:bg-white/10"}`} data-testid={`admin-nav-${key}`}>
              <Icon className="h-[18px] w-[18px]" /> {label}
            </button>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3 flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-lisungi-yellow text-navy flex items-center justify-center font-bold text-sm">{(user?.name || "A")[0]}</div>
          <div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{user?.name}</p><p className="text-[10px] text-white/50 font-mono">{user?.role}</p></div>
          <button onClick={() => navigate("/app")} className="text-white/50 hover:text-lisungi-yellow p-1" title="Espace utilisateur"><ExternalLink className="h-4 w-4" /></button>
          <button onClick={async () => { await logout(); navigate("/"); }} className="text-white/50 hover:text-lisungi-yellow p-1" data-testid="admin-logout"><LogOut className="h-4 w-4" /></button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="lg:hidden flex items-center gap-3 bg-navy px-4 h-14 shrink-0">
          <button onClick={() => setMobileOpen(true)} className="text-white p-1.5 rounded-lg hover:bg-white/10" data-testid="admin-mobile-menu" aria-label="Ouvrir le menu"><Menu className="h-6 w-6" /></button>
          <span className="text-white font-semibold truncate">{currentLabel}</span>
          <span className="ml-auto text-[10px] font-mono text-lisungi-yellow uppercase tracking-wider">LARSICOM</span>
        </div>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          {section === "overview" && <Overview />}
          {section === "users" && <UsersSection />}
          {section === "admins" && <AdminsSection />}
          {section === "knowledge" && <KnowledgeSection />}
          {section === "messages" && <MessagesSection />}
          {section === "newsletters" && <NewslettersSection />}
          {section === "emails" && <EmailsSection />}
          {section === "billing" && <BillingSection />}
          {section === "content" && <ContentSection />}
          {section === "media" && <MediaSection />}
          {section === "activity" && <ActivitySection />}
        </main>
      </div>
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-navy" />;
  return (
    <div className="animate-fade-up">
      <h1 className="font-serif text-3xl font-bold text-navy">Vue générale</h1>
      <p className="text-muted2 mt-1">Tableau de bord institutionnel Lisungi AI.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Stat label="Utilisateurs" value={s.users} sub={`${s.active} actifs`} />
        <Stat label="Projets" value={s.projects} />
        <Stat label="Conversations IA" value={s.conversations} />
        <Stat label="Documents" value={s.documents} />
        <Stat label="Base de connaissances" value={s.kb_documents} sub="ressources" />
        <Stat label="Abonnés payants" value={s.paid_users} />
        <Stat label="Crédits consommés" value={s.credits_spent} />
        <Stat label="Revenu mensuel (MRR)" value={`$${s.mrr}`} />
      </div>
      <div className="mt-6 bg-white rounded-2xl border border-[#E5E7EB] p-6">
        <p className="font-semibold text-navy mb-4">Répartition des offres</p>
        <div className="flex flex-wrap gap-4">
          {Object.entries(s.plan_distribution).map(([k, v]) => <div key={k} className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-lisungi-yellow" /><span className="text-sm text-ink capitalize">{k}: <strong>{v}</strong></span></div>)}
        </div>
      </div>
    </div>
  );
}

function UsersSection() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [nc, setNc] = useState({ name: "", email: "", role: "student", password: "" });
  const load = (query = "") => api.get("/admin/users", { params: query ? { q: query } : {} }).then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const update = async (uid, changes) => { try { await api.put(`/admin/users/${uid}`, changes); toast.success("Utilisateur mis à jour"); load(q); } catch (e) { toast.error(apiErr(e)); } };
  const remove = async (uid) => { await api.delete(`/admin/users/${uid}`); toast.success("Supprimé"); load(q); };
  const createContact = async () => {
    if (!nc.name || !nc.email) { toast.error("Nom et e-mail requis"); return; }
    try { await api.post("/admin/users", nc); toast.success("Contact créé"); setNc({ name: "", email: "", role: "student", password: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="animate-fade-up">
      <h1 className="font-serif text-3xl font-bold text-navy">Utilisateurs & Contacts</h1>
      <div className="mt-4 flex gap-2 max-w-md"><Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)} placeholder="Rechercher par nom ou e-mail…" data-testid="admin-user-search" /><Button onClick={() => load(q)} className="bg-navy text-white">Chercher</Button></div>
      <div className="mt-4 bg-white rounded-2xl border border-[#E5E7EB] p-4 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs text-muted2">Nom</Label><Input value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} className="h-9 w-44" data-testid="new-contact-name" /></div>
        <div><Label className="text-xs text-muted2">E-mail</Label><Input value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} className="h-9 w-52" data-testid="new-contact-email" /></div>
        <div><Label className="text-xs text-muted2">Mot de passe (optionnel)</Label><Input value={nc.password} onChange={(e) => setNc({ ...nc, password: e.target.value })} className="h-9 w-44" data-testid="new-contact-password" placeholder="Lisungi2026" /></div>
        <Select value={nc.role} onValueChange={(v) => setNc({ ...nc, role: v })}><SelectTrigger className="h-9 w-36" data-testid="new-contact-role"><SelectValue /></SelectTrigger><SelectContent>{["student", "researcher", "supervisor", "user"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
        <Button onClick={createContact} className="bg-navy text-white rounded-full gap-1 h-9" data-testid="create-contact-btn"><Plus className="h-4 w-4" /> Ajouter</Button>
      </div>
      <div className="mt-5 bg-white rounded-2xl border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-lisungi-bg text-muted2 text-xs uppercase font-mono"><tr><th className="text-left p-3">Utilisateur</th><th className="text-left p-3">Rôle</th><th className="text-left p-3">Offre</th><th className="text-left p-3">Crédits</th><th className="text-left p-3">Projets</th><th className="text-left p-3">Statut</th><th className="p-3"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-t border-[#F1F1F1]" data-testid={`admin-user-${u.user_id}`}>
                <td className="p-3"><p className="font-medium text-navy">{u.name}</p><p className="text-xs text-muted2">{u.email}</p></td>
                <td className="p-3">
                  <Select value={u.role} onValueChange={(v) => update(u.user_id, { role: v })}>
                    <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{["user", "student", "researcher", "supervisor", "admin", "scientific_admin", "editor", "lab_admin", "director", "super_admin"].map((r) => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3 text-xs font-mono">{u.plan}</td>
                <td className="p-3">{u.credits}</td>
                <td className="p-3">{u.project_count}</td>
                <td className="p-3"><button onClick={() => update(u.user_id, { status: u.status === "active" ? "suspended" : "active" })} className={`text-xs px-2 py-1 rounded font-mono ${u.status === "active" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>{u.status}</button></td>
                <td className="p-3"><button onClick={() => remove(u.user_id)} className="text-muted2 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminsSection() {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin" });
  const load = () => api.get("/admin/administrators").then((r) => setAdmins(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => { try { await api.post("/admin/administrators", form); toast.success("Administrateur créé"); setForm({ name: "", email: "", password: "", role: "admin" }); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="animate-fade-up">
      <h1 className="font-serif text-3xl font-bold text-navy">Administrateurs</h1>
      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]"><thead className="bg-lisungi-bg text-muted2 text-xs uppercase font-mono"><tr><th className="text-left p-3">Nom</th><th className="text-left p-3">E-mail</th><th className="text-left p-3">Rôle</th><th className="text-left p-3">Dernière connexion</th></tr></thead>
            <tbody>{admins.map((a) => <tr key={a.user_id} className="border-t border-[#F1F1F1]"><td className="p-3 font-medium text-navy">{a.name}</td><td className="p-3 text-muted2">{a.email}</td><td className="p-3 font-mono text-xs">{a.role}</td><td className="p-3 text-xs text-muted2">{a.last_login ? new Date(a.last_login).toLocaleDateString("fr-FR") : "—"}</td></tr>)}</tbody>
          </table>
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
          <p className="font-semibold text-navy mb-3">Créer un administrateur</p>
          <div className="space-y-3">
            <Input placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-create-name" />
            <Input placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-create-email" />
            <Input placeholder="Mot de passe" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-create-password" />
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="admin-create-role"><SelectValue /></SelectTrigger><SelectContent>{["admin", "scientific_admin", "editor", "lab_admin", "director", "super_admin"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
            <Button onClick={create} className="w-full bg-navy text-white rounded-full gap-2" data-testid="admin-create-submit"><Plus className="h-4 w-4" /> Créer</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KnowledgeSection() {
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category: CATEGORIES[0] });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const load = () => api.get("/knowledge/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);
  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || !form.title) { toast.error("Titre et fichier requis"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("title", form.title); fd.append("description", form.description); fd.append("category", form.category); fd.append("published", "true");
    try { await api.post("/knowledge/documents", fd); toast.success("Document ajouté — traitement en cours"); setForm({ title: "", description: "", category: CATEGORIES[0] }); fileRef.current.value = ""; load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };
  const togglePub = async (d) => { await api.put(`/knowledge/documents/${d.id}`, { published: !d.published }); load(); };
  const remove = async (id) => { await api.delete(`/knowledge/documents/${id}`); toast.success("Supprimé"); load(); };
  return (
    <div className="animate-fade-up">
      <h1 className="font-serif text-3xl font-bold text-navy">Base de connaissances</h1>
      <p className="text-muted2 mt-1">Ajoutez des ressources : import → extraction → segmentation → indexation → RAG.</p>
      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
          <p className="font-semibold text-navy mb-3">Ajouter une ressource</p>
          <div className="space-y-3">
            <Input placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="kb-title" />
            <Textarea placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="kb-description" />
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="kb-category"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="text-sm" data-testid="kb-file" />
            <Button onClick={upload} disabled={uploading} className="w-full bg-navy text-white rounded-full gap-2" data-testid="kb-upload">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importer & indexer</Button>
          </div>
        </div>
        <div className="lg:col-span-2 space-y-3">
          {docs.map((d) => (
            <div key={d.id} className="bg-white rounded-2xl border border-[#E5E7EB] p-4" data-testid={`kb-doc-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0"><p className="font-semibold text-navy truncate">{d.title}</p><p className="text-xs text-muted2">{d.category} · {d.chunk_count || 0} segments · par {d.added_by}</p></div>
                <span className={`text-xs px-2 py-1 rounded font-mono shrink-0 ${STATUS_COLOR[d.status] || "bg-gray-100"}`}>{d.status}</span>
              </div>
              {d.error && <p className="text-xs text-red-500 mt-1">{d.error}</p>}
              <div className="flex items-center gap-3 mt-3">
                <div className="flex items-center gap-2"><Switch checked={d.published} onCheckedChange={() => togglePub(d)} data-testid={`kb-publish-${d.id}`} /><span className="text-xs text-muted2">{d.published ? "Publié" : "Dépublié"}</span></div>
                <button onClick={() => remove(d.id)} className="text-muted2 hover:text-red-500 ml-auto"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          {docs.length === 0 && <div className="text-center py-16 text-muted2 bg-white rounded-2xl border border-dashed border-[#E5E7EB]"><Database className="h-8 w-8 mx-auto mb-2" />Aucune ressource. Importez le premier guide méthodologique.</div>}
        </div>
      </div>
    </div>
  );
}

function MessagesSection() {
  const [form, setForm] = useState({ title: "", content: "", category: "Lisungi" });
  const send = async (type) => {
    if (!form.title || !form.content) { toast.error("Titre et contenu requis"); return; }
    try { const r = await api.post(`/admin/${type}/broadcast`, form); toast.success(type === "messages" ? `Message envoyé à ${r.data.sent} utilisateurs` : "Notification diffusée"); setForm({ title: "", content: "", category: "Lisungi" }); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="animate-fade-up max-w-2xl">
      <h1 className="font-serif text-3xl font-bold text-navy">Messages & Notifications</h1>
      <p className="text-muted2 mt-1">Diffusez une communication institutionnelle à tous les utilisateurs.</p>
      <div className="mt-6 bg-white rounded-2xl border border-[#E5E7EB] p-6 space-y-4">
        <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="broadcast-title" /></div>
        <div><Label>Contenu</Label><Textarea rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="mt-1" data-testid="broadcast-content" /></div>
        <div><Label>Catégorie</Label><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{["Lisungi", "LARSICOM", "maintenance", "nouveauté", "science", "abonnement"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        <div className="flex gap-2">
          <Button onClick={() => send("messages")} className="bg-navy text-white rounded-full gap-2 flex-1" data-testid="send-message"><Mail className="h-4 w-4" /> Envoyer aux boîtes de réception</Button>
          <Button onClick={() => send("notifications")} variant="outline" className="rounded-full gap-2 border-navy text-navy"><Bell className="h-4 w-4" /> Notifier</Button>
        </div>
      </div>
    </div>
  );
}

function NewslettersSection() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", intro: "", body: "" });
  const load = () => api.get("/admin/newsletters").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => { if (!form.title) return; await api.post("/admin/newsletters", form); toast.success("Brouillon enregistré"); setForm({ title: "", intro: "", body: "" }); load(); };
  const send = async (id) => { const r = await api.post(`/admin/newsletters/${id}/send`); toast.success(`Envoyée à ${r.data.sent} abonnés (simulé)`); load(); };
  return (
    <div className="animate-fade-up">
      <h1 className="font-serif text-3xl font-bold text-navy">Newsletters</h1>
      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 space-y-3">
          <p className="font-semibold text-navy">Nouvelle newsletter</p>
          <Input placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="nl-title" />
          <Textarea placeholder="Introduction" rows={2} value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} />
          <Textarea placeholder="Contenu (articles, actualités, conseils…)" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          <Button onClick={create} className="w-full bg-navy text-white rounded-full gap-2" data-testid="nl-save"><Save className="h-4 w-4" /> Enregistrer comme brouillon</Button>
        </div>
        <div className="space-y-3">
          {items.map((n) => (
            <div key={n.id} className="bg-white rounded-2xl border border-[#E5E7EB] p-4">
              <div className="flex items-center justify-between"><p className="font-semibold text-navy">{n.title}</p><span className="text-xs font-mono px-2 py-0.5 rounded bg-navy/5 text-navy">{n.status}</span></div>
              <p className="text-sm text-muted2 mt-1 line-clamp-2">{n.intro}</p>
              <div className="flex items-center gap-3 mt-3 text-xs text-muted2 font-mono">{n.sent_count > 0 && <span>{n.sent_count} envoyés</span>}</div>
              {n.status !== "sent" && <Button onClick={() => send(n.id)} size="sm" className="mt-3 bg-lisungi-yellow text-navy rounded-full gap-1" data-testid={`nl-send-${n.id}`}><Send className="h-3.5 w-3.5" /> Envoyer</Button>}
            </div>
          ))}
          {items.length === 0 && <p className="text-muted2 text-sm">Aucune newsletter.</p>}
        </div>
      </div>
    </div>
  );
}

function EmailsSection() {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);
  const selected = users.find((u) => u.user_id === uid);
  const send = async () => {
    if (!uid) { toast.error("Sélectionnez un destinataire"); return; }
    if (!subject.trim() || !message.trim()) { toast.error("Sujet et message requis"); return; }
    setSending(true);
    try { const r = await api.post("/admin/email/send", { user_id: uid, subject, message }); toast.success(`E-mail envoyé à ${r.data.to}`); setSubject(""); setMessage(""); }
    catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };
  const welcome = async () => {
    if (!uid) { toast.error("Sélectionnez un destinataire"); return; }
    setSending(true);
    try { const r = await api.post(`/admin/email/welcome/${uid}`); toast.success(`E-mail de bienvenue envoyé à ${r.data.to}`); }
    catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };
  return (
    <div className="animate-fade-up max-w-2xl">
      <h1 className="font-serif text-3xl font-bold text-navy">E-mails</h1>
      <p className="text-muted2 mt-1">Envoyez un e-mail personnalisé, aux couleurs de Lisungi, à un utilisateur inscrit. Design, logo et signature LARSICOM inclus automatiquement.</p>
      <div className="mt-6 bg-white rounded-2xl border border-[#E5E7EB] p-6 space-y-4">
        <div>
          <Label>Destinataire</Label>
          <Select value={uid} onValueChange={setUid}>
            <SelectTrigger className="mt-1" data-testid="email-recipient"><SelectValue placeholder="Choisir un utilisateur inscrit…" /></SelectTrigger>
            <SelectContent className="max-h-72">{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} — {u.email}</SelectItem>)}</SelectContent>
          </Select>
          {selected && <p className="text-xs text-muted2 mt-1">Sera envoyé à <strong>{selected.email}</strong></p>}
        </div>
        <div><Label>Sujet</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" placeholder="Ex. : Nouveautés sur Lisungi AI" data-testid="email-subject" /></div>
        <div><Label>Message</Label><Textarea rows={6} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1" placeholder="Écrivez votre message. Chaque paragraphe sera mis en forme automatiquement." data-testid="email-message" /></div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={send} disabled={sending} className="bg-navy text-white rounded-full gap-2 flex-1" data-testid="email-send"><Send className="h-4 w-4" /> {sending ? "Envoi…" : "Envoyer l'e-mail"}</Button>
          <Button onClick={welcome} disabled={sending} variant="outline" className="rounded-full gap-2 border-navy text-navy" data-testid="email-welcome"><Mail className="h-4 w-4" /> Envoyer l'e-mail de bienvenue</Button>
        </div>
        <p className="text-xs text-muted2 border-t border-[#F1F1F1] pt-3">Pour préserver la réputation d'envoi, les e-mails sont transactionnels et adressés individuellement aux utilisateurs inscrits. Les envois de masse ne sont pas activés.</p>
      </div>
    </div>
  );
}

function BillingSection() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)); }, []);
  const save = async () => { await api.put("/admin/settings", { plans: settings.plans, feature_costs: settings.feature_costs, promo_message: settings.promo_message, pricing_visible: settings.pricing_visible, promo_enabled: settings.promo_enabled, launch_free: settings.launch_free }); toast.success("Paramètres enregistrés"); };
  if (!settings) return <Loader2 className="h-6 w-6 animate-spin text-navy" />;
  const set = (k, v) => setSettings({ ...settings, [k]: v });
  return (
    <div className="animate-fade-up">
      <h1 className="font-serif text-3xl font-bold text-navy">Abonnements & Crédits</h1>
      <p className="text-muted2 mt-1">Modifiez sans coder les prix, crédits, l'affichage des tarifs et la promotion.</p>
      <div className="mt-6 bg-white rounded-2xl border border-[#E5E7EB] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div><p className="font-medium text-navy">Afficher les tarifs sur le site</p><p className="text-xs text-muted2">Masque/affiche la section Offres de la page d'accueil.</p></div>
          <Switch checked={settings.pricing_visible !== false} onCheckedChange={(v) => set("pricing_visible", v)} data-testid="toggle-pricing" />
        </div>
        <div className="flex items-center justify-between border-t border-[#F1F1F1] pt-4">
          <div><p className="font-medium text-navy">Lancement gratuit (paiement désactivé)</p><p className="text-xs text-muted2">Tous les plans sont gratuits pendant la période de lancement.</p></div>
          <Switch checked={settings.launch_free !== false} onCheckedChange={(v) => set("launch_free", v)} data-testid="toggle-launch-free" />
        </div>
        <div className="border-t border-[#F1F1F1] pt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="font-medium text-navy">Message promotionnel</p>
            <div className="flex items-center gap-3">
              <button onClick={() => set("promo_message", "")} className="text-xs text-red-500 hover:underline" data-testid="promo-delete">Supprimer</button>
              <Switch checked={settings.promo_enabled === true} onCheckedChange={(v) => set("promo_enabled", v)} data-testid="toggle-promo" />
            </div>
          </div>
          <Textarea value={settings.promo_message || ""} onChange={(e) => set("promo_message", e.target.value)} rows={2} data-testid="promo-message" placeholder="Ex. : Tous les plans sont gratuits pendant 1 mois." />
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
          <p className="font-semibold text-navy mb-3">Offres</p>
          {settings.plans.map((p, i) => (
            <div key={p.key} className="flex items-center gap-2 mb-3">
              <span className="w-24 text-sm text-navy font-medium">{p.name}</span>
              <Input type="number" value={p.price ?? ""} onChange={(e) => { const pl = [...settings.plans]; pl[i] = { ...p, price: e.target.value === "" ? null : parseFloat(e.target.value) }; setSettings({ ...settings, plans: pl }); }} className="h-8 w-20" placeholder="$" />
              <Input type="number" value={p.credits} onChange={(e) => { const pl = [...settings.plans]; pl[i] = { ...p, credits: parseInt(e.target.value || 0) }; setSettings({ ...settings, plans: pl }); }} className="h-8 w-24" placeholder="crédits" />
              <span className="text-xs text-muted2">crédits</span>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
          <p className="font-semibold text-navy mb-3">Coût par fonctionnalité (crédits)</p>
          {Object.entries(settings.feature_costs).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between mb-2"><span className="text-sm text-ink">{k}</span>
              <Input type="number" value={v} onChange={(e) => setSettings({ ...settings, feature_costs: { ...settings.feature_costs, [k]: parseInt(e.target.value || 0) } })} className="h-8 w-20" data-testid={`cost-${k}`} /></div>
          ))}
        </div>
      </div>
      <div className="mt-5 bg-white rounded-2xl border border-[#E5E7EB] p-5">
        <Label>Message promotionnel Premium</Label>
        <Textarea value={settings.promo_message} onChange={(e) => setSettings({ ...settings, promo_message: e.target.value })} rows={2} className="mt-2" data-testid="promo-message" />
      </div>
      <Button onClick={save} className="mt-4 bg-navy text-white rounded-full gap-2" data-testid="billing-save"><Save className="h-4 w-4" /> Enregistrer</Button>
    </div>
  );
}

const CONTENT_SCHEMA = [
  ["Header / Navigation", [
    ["nav_larsicom", "Lien : Le LARSICOM", 0], ["nav_features", "Lien : Fonctionnalités", 0],
    ["nav_pricing", "Lien : Offres", 0], ["nav_responsible", "Lien : Usage responsable", 0],
    ["nav_login", "Bouton : Se connecter", 0], ["nav_cta", "Bouton : Commencer", 0],
  ]],
  ["Hero (bannière)", [
    ["hero_lead", "Ligne d'introduction (au-dessus du titre)", 0],
    ["hero_title", "Titre principal", 1],
    ["hero_subtitle", "Sous-titre", 1],
    ["hero_cta", "Bouton principal", 0], ["hero_secondary", "Bouton secondaire (LARSICOM)", 0],
  ]],
  ["Bande de positionnement", [["positioning_steps", "Étapes (séparées par |)", 0]]],
  ["Section Fonctionnalités", [
    ["features_eyebrow", "Sur-titre", 0], ["features_title", "Titre", 1],
    ["feature_1_title", "Fonction 1 — titre", 0], ["feature_1_desc", "Fonction 1 — description", 1],
    ["feature_2_title", "Fonction 2 — titre", 0], ["feature_2_desc", "Fonction 2 — description", 1],
    ["feature_3_title", "Fonction 3 — titre", 0], ["feature_3_desc", "Fonction 3 — description", 1],
    ["feature_4_title", "Fonction 4 — titre", 0], ["feature_4_desc", "Fonction 4 — description", 1],
    ["feature_5_title", "Fonction 5 — titre", 0], ["feature_5_desc", "Fonction 5 — description", 1],
    ["feature_6_title", "Fonction 6 — titre", 0], ["feature_6_desc", "Fonction 6 — description", 1],
  ]],
  ["Section LARSICOM", [
    ["larsicom_badge", "Badge", 0], ["larsicom_title", "Titre", 1], ["larsicom_intro", "Introduction", 1],
    ["larsicom_vocation", "Vocation", 1], ["larsicom_role", "Rôle dans Lisungi", 1],
    ["larsicom_website", "Lien du site LARSICOM", 0], ["larsicom_website_label", "Libellé du lien", 0],
  ]],
  ["Section Offres", [["pricing_eyebrow", "Sur-titre", 0], ["pricing_title", "Titre", 1]]],
  ["Nos Partenaires", [["partners_title", "Titre de la section", 0]]],
  ["Page de connexion / inscription", [
    ["auth_side_title", "Titre (panneau bleu)", 1], ["auth_side_text", "Texte (panneau bleu)", 1],
    ["auth_login_title", "Titre — Connexion", 0], ["auth_login_sub", "Sous-titre — Connexion", 1],
    ["auth_register_title", "Titre — Créer un compte", 0], ["auth_register_sub", "Sous-titre — Créer un compte", 1],
  ]],
  ["Application mobile (APK Android)", [
    ["apk_url", "Lien de téléchargement du fichier .apk (URL publique)", 0],
    ["apk_button_label", "Libellé du bouton de téléchargement", 0],
  ]],
  ["Pied de page", [
    ["footer_tagline", "Slogan", 1], ["footer_nav_title", "Titre colonne navigation", 0],
    ["footer_access_title", "Titre colonne accès", 0], ["footer_copyright", "Copyright", 0],
  ]],
];

function ContentSection() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/content").then((r) => setC(r.data)); }, []);
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/content", c); setC(r.data); toast.success("Page d'accueil mise à jour"); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  if (!c) return <Loader2 className="h-6 w-6 animate-spin text-navy" />;
  return (
    <div className="animate-fade-up max-w-3xl pb-24">
      <div className="flex items-center justify-between sticky top-0 bg-lisungi-bg py-2 z-10">
        <div><h1 className="font-serif text-3xl font-bold text-navy">Contenu de la page d'accueil</h1><p className="text-muted2 mt-1">Modifiez chaque texte, du header au pied de page.</p></div>
        <Button onClick={save} disabled={saving} className="bg-navy text-white rounded-full gap-2" data-testid="content-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer</Button>
      </div>
      <div className="mt-4 space-y-5">
        {CONTENT_SCHEMA.map(([group, fields]) => (
          <div key={group} className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
            <p className="font-semibold text-navy mb-4 text-sm uppercase tracking-wide font-mono">{group}</p>
            <div className="space-y-3">
              {fields.map(([key, label, area]) => (
                <div key={key}>
                  <Label className="text-xs text-muted2">{label}</Label>
                  {area ? (
                    <Textarea value={c[key] || ""} onChange={(e) => setC({ ...c, [key]: e.target.value })} rows={2} className="mt-1" data-testid={`content-${key}`} />
                  ) : (
                    <Input value={c[key] || ""} onChange={(e) => setC({ ...c, [key]: e.target.value })} className="mt-1" data-testid={`content-${key}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MediaSection() {
  const [items, setItems] = useState([]);
  const [slot, setSlot] = useState("logo_lisungi");
  const fileRef = useRef();
  const load = () => api.get("/admin/media").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const upload = async () => {
    const file = fileRef.current?.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("slot", slot);
    try { await api.post("/admin/media", fd); toast.success("Média ajouté"); fileRef.current.value = ""; load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => { await api.delete(`/admin/media/${id}`); load(); };
  return (
    <div className="animate-fade-up">
      <h1 className="font-serif text-3xl font-bold text-navy">Médiathèque</h1>
      <div className="mt-4 bg-white rounded-2xl border border-[#E5E7EB] p-5 flex flex-wrap items-end gap-3">
        <div><Label>Emplacement</Label><Select value={slot} onValueChange={setSlot}><SelectTrigger className="mt-1 w-64" data-testid="media-slot"><SelectValue /></SelectTrigger><SelectContent>{[["logo_lisungi", "Logo Lisungi AI"], ["logo_larsicom", "Logo LARSICOM"], ["favicon", "Favicon (onglet navigateur)"], ["cover_image", "Image de couverture (accueil)"], ["larsicom_image", "Photo du laboratoire LARSICOM"], ["auth_image", "Image page de connexion"], ["partner", "Logo partenaire"], ["gallery", "Galerie"]].map(([s, lbl]) => <SelectItem key={s} value={s}>{lbl}</SelectItem>)}</SelectContent></Select></div>
        <input ref={fileRef} type="file" accept="image/*" className="text-sm" data-testid="media-file" />
        <Button onClick={upload} className="bg-navy text-white rounded-full gap-2"><Upload className="h-4 w-4" /> Ajouter</Button>
      </div>
      <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-5">
        {items.map((m) => (
          <div key={m.id} className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
            <img src={fileUrl(m.url)} alt="" className="w-full h-32 object-cover bg-lisungi-bg" />
            <div className="p-3 flex items-center justify-between"><span className="text-xs font-mono text-muted2 truncate">{m.slot}</span><button onClick={() => remove(m.id)} className="text-muted2 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></div>
          </div>
        ))}
        {items.length === 0 && <p className="text-muted2 text-sm col-span-full">Aucun média.</p>}
      </div>
    </div>
  );
}

function ActivitySection() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/activity").then((r) => setItems(r.data)); }, []);
  return (
    <div className="animate-fade-up max-w-3xl">
      <h1 className="font-serif text-3xl font-bold text-navy">Journal d'activité</h1>
      <div className="mt-6 bg-white rounded-2xl border border-[#E5E7EB] divide-y divide-[#F1F1F1]">
        {items.map((a) => (
          <div key={a.id} className="p-3 flex items-center gap-3 text-sm">
            <Activity className="h-4 w-4 text-lisungi-yellow shrink-0" />
            <span className="font-mono text-xs bg-navy/5 text-navy px-2 py-0.5 rounded">{a.action}</span>
            <span className="text-muted2 truncate">{a.user_email || "—"}</span>
            <span className="text-xs text-muted2 ml-auto font-mono shrink-0">{new Date(a.created_at).toLocaleString("fr-FR")}</span>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 text-muted2 text-sm">Aucune activité enregistrée.</p>}
      </div>
    </div>
  );
}
